import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { useNotify, useRefresh } from 'react-admin';
import { releaseCandidate, toErrorMessage } from './orderActionsApi';
import { withVariantScopeNote } from './variantScope';

/**
 * Zwolnienie wariantu pojazdu.
 *
 * ## Dlaczego to dochodzi
 *
 * Endpoint zwalniania istniał od porcie modułu, funkcja `releaseCandidate`
 * w `orderActionsApi.ts` też — i **nic jej nie wołało**. Tabela wariantów
 * pokazywała kolumnę „Powód zwolnienia", którą mógł wypełnić wyłącznie
 * automat przy obsadzaniu. Planista, który dodał zły wariant albo któremu
 * wariant odpadł, nie miał jak tego odnotować.
 *
 * ## Dlaczego pytamy o powód
 *
 * Bo jest kolumna, która na niego czeka, i bo to jedyny ślad, dlaczego auto
 * wypadło z rozważań. Powód jest **nieobowiązkowy** — wymuszanie go kończy
 * się wpisywaniem kropki — ale pytamy o niego wprost, zanim zwolnimy, a nie
 * po fakcie.
 *
 * ## Czego tu nie ma
 *
 * Zwalniania pojazdu, który jest obsadzony. Odmawia tego serwer (zostałby
 * rekord obsadzony autem bez aktywnej kandydatury), a przycisku dla takiego
 * wiersza po prostu nie pokazujemy — żeby planista nie klikał w coś, co i tak
 * zostanie odrzucone.
 *
 * ## Także z karty pojazdu
 *
 * Omówienie 01.10.2026 [1:41:06]: w „Rekomendowanych zleceniach dla
 * pojazdu” przy zleceniu, do którego auto już jest wariantem, brakowało
 * usunięcia wariantu. To ta sama operacja (zwolnienie z powodem, wpis
 * w dzienniku zlecenia, `FTL_PLANNING`), tylko z innym podpisem przycisku
 * (`label`) i z odświeżeniem listy rekomendacji zamiast rekordu
 * (`onReleased`). Tam stoi jako ikona z podpisem w dymku (`iconOnly`,
 * 06.10.2026) — słowami poszerzał kolumnę akcji tak, że tabela nie
 * mieściła się w oknie 1366 px.
 */
export const ReleaseVariantButton = ({
  userOfferId,
  carId,
  registrationNumber: numerRejestracyjny,
  label = 'Zwolnij',
  iconOnly = false,
  onReleased,
}: {
  userOfferId: number | string;
  carId: number;
  registrationNumber: string;
  /** Podpis przycisku; domyślnie „Zwolnij” (tabela wariantów zlecenia). */
  label?: string;
  /** Ikona z podpisem (`label`) w dymku i jako nazwa przycisku zamiast słów. */
  iconOnly?: boolean;
  /** Po zwolnieniu — zamiast odświeżenia rekordu react-admina. */
  onReleased?: () => void | Promise<void>;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const release = () =>
    void (async () => {
      setBusy(true);
      try {
        const released: unknown = await releaseCandidate(userOfferId, carId, { reason });
        notify(withVariantScopeNote(`Wariant ${numerRejestracyjny} zwolniony`, released), { type: 'success' });
        setDialogOpen(false);
        setReason('');
        if (onReleased) await onReleased();
        else refresh();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <>
      {iconOnly ? (
        <Tooltip title={label}>
          <IconButton size="small" aria-label={label} onClick={() => setDialogOpen(true)}>
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button size="small" color="inherit" sx={{ whiteSpace: 'nowrap' }} onClick={() => setDialogOpen(true)}>
          {label}
        </Button>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Zwolnić wariant {numerRejestracyjny}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Wariant zostanie na liście ze stanem „Zwolniony”, więc historia
            wariantów nie zniknie. Powód pojawi się w kolumnie „Powód
            zwolnienia”.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Powód (opcjonalny)"
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            helperText="Np. „kierowca na urlopie”, „auto w serwisie”, „za daleko”"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={busy}
            color="inherit"
          >
            Anuluj
          </Button>
          <Button onClick={release} disabled={busy} variant="contained">
            Zwolnij wariant
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
